import React from "react";
import { ButtonBase, TwoColumnSectionBase, ModalBase } from "@reusejs/react";
import { useRouter } from "next/router";
import { DateTime } from "luxon";
import { useSelector } from "react-redux";
import CancellationModal from "./ConfirmCancellation";
import { updateAuctionEventStatus } from "services";

export interface AuctionEventCardProps {
  property: any;
  event: any;
  callback: any;
}

export default function AuctionEventCard(props: AuctionEventCardProps) {
  const router = useRouter();

  return (
    <TwoColumnSectionBase
      firstColumnEl={<Content {...props} />}
      secondColumnEl={<Action {...props} />}
      twoColumnSectionBaseClasses={{
        firstElWrapperStyles: "w-full md:w-3/4 self-stretch",
        secondElWrapperStyles: "w-full md:w-1/4 border-l self-stretch p-4",
        columnsFlexWrapperStyles:
          "flex items-start justify-between border shadow rounded-lg flex-col md:flex-row bg-white",
      }}
    />
  );
}

const Content = (props: any) => {
  const getStatusItem = (status: string) => {
    let element;
    switch (status) {
      case "pending":
        element = (
          <div className="text-left px-4 py-2 bg-gray-100 rounded-bl-lg flex justify-between items-center">
            <div className="flex">
              <div className="text-xs text-gray-500">
                Update:{" "}
                <span className="font-semibold">
                  Auction event has been created and waiting for approval.{" "}
                </span>
              </div>
            </div>
          </div>
        );
        break;

      case "accepted":
        element = (
          <div className="text-left px-4 py-2 bg-green-100 rounded-bl-lg flex justify-between items-center">
            <div className="flex">
              <div className="text-xs text-green-500">
                Status:{" "}
                <span className="font-semibold">
                  Auction event has been accepted by the approver.
                </span>
              </div>
            </div>
          </div>
        );
        break;

      case "rejected":
        element = (
          <div className="text-left px-4 py-2 bg-red-100 rounded-bl-lg flex justify-between items-center">
            <div className="flex">
              <div className="text-xs text-red-500">
                Status:{" "}
                <span className="font-semibold">
                  Auction event has been rejected by the approver.
                </span>
              </div>
            </div>
          </div>
        );
        break;

      case "live":
        element = (
          <div className="text-left px-4 py-2 bg-blue-100 rounded-bl-lg flex justify-between items-center">
            <div className="flex">
              <div className="text-xs text-blue-500">
                Status:{" "}
                <span className="font-semibold">Auction event is live.</span>
              </div>
            </div>
          </div>
        );
        break;

      case "end":
        element = (
          <div className="text-left px-4 py-2 bg-red-100 rounded-bl-lg flex justify-between items-center">
            <div className="flex">
              <div className="text-xs text-red-500">
                Status:{" "}
                <span className="font-semibold">Auction event has ended.</span>
              </div>
            </div>
          </div>
        );
        break;

      case "cancelled":
        element = (
          <div className="text-left px-4 py-2 bg-red-100 rounded-bl-lg flex justify-between items-center">
            <div className="flex">
              <div className="text-xs text-red-500">
                Status:{" "}
                <span className="font-semibold">
                  Auction event has been cancelled.
                </span>
              </div>
            </div>
          </div>
        );
        break;

      default:
        break;
    }
    return element;
  };

  return (
    <div className="flex-1">
      <div className="text-left px-4 py-2 border-b text-xs text-gray-500">
        Event ID: {props?.event?.listing_id}
      </div>
      <div className="text-left border-b px-4 py-4 flex justify-between">
        <div className="flex flex-col">
          <div className="text-xs text-gray-500">Event Name</div>
          <div className="text-sm break-all">{props?.event?.event_name}</div>
        </div>
      </div>
      <div className="text-left border-b px-4 flex justify-between">
        <div className="flex basis-1/2 border-r py-4 flex-col">
          <div className="text-xs text-gray-500">
            Bidding Start Date and Time
          </div>
          <div className="text-sm pt-1">
            <div className="flex items-center divide-x divide-gray-400">
              <div className="pr-3">
                {props?.event?.start_date_time &&
                  DateTime.fromISO(props?.event?.start_date_time)
                    .setZone("Asia/Kolkata")
                    .toFormat("dd MMMM yyyy")}
              </div>
              <div className="px-3 text-xs">
                {props?.event?.start_date_time &&
                  `${DateTime.fromISO(props?.event?.start_date_time)
                    .setZone("Asia/Kolkata")
                    .toFormat("hh:mm a")} (IST)`}
              </div>
            </div>
          </div>
        </div>
        <div className="flex basis-1/2 py-4 flex-col pl-6">
          <div className="text-xs text-gray-500">Bidding End Date and Time</div>
          <div className="text-sm pt-1">
            <div className="flex items-center divide-x divide-gray-400">
              <div className="pr-3">
                {props?.event?.end_date_time &&
                  DateTime.fromISO(props?.event?.end_date_time)
                    .setZone("Asia/Kolkata")
                    .toFormat("dd MMMM yyyy")}
              </div>
              <div className="px-3 text-xs">
                {props?.event?.end_date_time &&
                  `${DateTime.fromISO(props?.event?.end_date_time)
                    .setZone("Asia/Kolkata")
                    .toFormat("hh:mm a")} (IST)`}
              </div>
            </div>
          </div>
        </div>
      </div>
      {getStatusItem(props?.event?.status)}
    </div>
  );
};

const Action = (props: any) => {
  const router = useRouter();
  const user = useSelector((state: any) => {
    return state.bankerAuth.user;
  });

  const getStatusItem = (status: string) => {
    let element;

    switch (status) {
      case "pending":
        element = (
          <div className="text-2xl font-bold text-gray-500">Pending</div>
        );
        break;

      case "accepted":
        element = (
          <div className="text-2xl font-bold text-green-500">Accepted</div>
        );
        break;

      case "rejected":
        element = (
          <div className="text-2xl font-bold text-red-500">Rejected</div>
        );
        break;

      case "live":
        element = <div className="text-2xl font-bold text-blue-500">Live</div>;
        break;

      case "end":
        element = <div className="text-2xl font-bold text-red-500">Ended</div>;
        break;

      case "cancelled":
        element = (
          <div className="text-2xl font-bold text-red-500">Cancelled</div>
        );
        break;

      default:
        break;
    }
    return element;
  };

  const handleCancel = async () => {
    try {
      await updateAuctionEventStatus(props?.event?.uuid, "cancelled", "");
      if (props.callback) {
        props.callback();
      }
    } catch (error) {
      console.log("check error", error);
    }
  };

  const openModal = async (
    type: string,
    title: string,
    message: string,
    color: string
  ) => {
    const SuccessModalWrapper = (props: any) => {
      return (
        <CancellationModal
          router={router}
          type={type}
          heading={title}
          description={message}
          callback={() => handleCancel()}
          color={color}
          {...props}
        />
      );
    };

    await ModalBase(
      {
        content: SuccessModalWrapper,
        timeout: 0,
        backgroundColor: "bg-black/30",
        backgroundOpacity: "opacity-70",
        modalBaseClasses: {
          background: "bg-white",
          border: "border-2 rounded-lg",
          alignment: "z-50  overflow-visible",
          padding: "p-0",
          small: "max-w-4xl",
        },
      },
      1000,
      "__next"
    );
  };

  return (
    <div className="flex flex-col h-full justify-between">
      <div>
        <div className="text-left flex justify-center">
          <div className="flex basis-1/2 flex-col items-center">
            <div className="text-base text-gray-500">Status</div>
            {getStatusItem(props?.event?.status)}
          </div>
        </div>
      </div>
      <div className="space-y-4">
        {["pending", "accepted", "rejected"].includes(props?.event?.status) &&
          props?.event?.approver !== user.uuid && (
            <ButtonBase
              variant="primary"
              type="submit"
              label={"Edit"}
              onClick={() =>
                router.push(
                  `/dashboard/events/edit/${props?.event?.listing_id}`
                )
              }
              buttonBaseClasses={{
                font: "text-base font-medium",
                backgroundColor: "bg-transparent",
                textColor: "text-gray-900 hover:text-blue-600",
                border: "border border-gray-900 hover:border-blue-600",
                width: "w-full",
                padding: "px-4 py-3",
              }}
            />
          )}
        {props?.event?.status === "live" && (
          <ButtonBase
            variant="primary"
            type="submit"
            label={"Auction Floor"}
            onClick={
              () =>
                window.open(
                  `/dashboard/live-events/${props?.event?.uuid}`,
                  "_blank"
                )
              // router.push(`/dashboard/live-events/${props?.event?.uuid}`)
            }
            buttonBaseClasses={{
              font: "text-base font-medium",
              backgroundColor: "bg-transparent",
              textColor: "text-gray-900 hover:text-blue-600",
              border: "border border-gray-900 hover:border-blue-600",
              width: "w-full",
              padding: "px-4 py-3",
            }}
          />
        )}
        {["pending", "accepted", "rejected", "live"].includes(
          props?.event?.status
        ) && (
          <ButtonBase
            variant="primary"
            type="submit"
            label={"Cancel"}
            onClick={() =>
              openModal(
                "edit",
                "Auction Event Cancellation",
                "Are you sure you want to cancel this event.",
                "bg-red-50 text-red-500"
              )
            }
            buttonBaseClasses={{
              font: "text-base font-medium",
              backgroundColor: "bg-red-600 hover:bg-red-700",
              textColor: "text-white",
              border: "any",
              width: "w-full",
              padding: "px-4 py-3",
            }}
          />
        )}
        <ButtonBase
          variant="primary"
          type="submit"
          label={"View Details"}
          onClick={() =>
            router.push(`/dashboard/events/${props?.event?.listing_id}`)
          } //listing_id
          buttonBaseClasses={{
            font: "text-base font-medium",
            backgroundColor: "bg-blue-600 hover:bg-blue-700",
            textColor: "text-white",
            border: "any",
            width: "w-full",
            padding: "px-4 py-3",
          }}
        />
      </div>
    </div>
  );
};
